import { Link } from "react-router-dom";
import { waProductLink } from "@/lib/whatsapp";
import { resolveImg } from "@/components/admin/ProductsTab";
import { useAuth } from "@/lib/auth";
import { addToCart } from "@/lib/api";
import { ShoppingCart, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const BADGE_COLORS = {
  HOT: "bg-[var(--primary)] text-white",
  NEW: "bg-[var(--secondary)] text-black",
  PREMIUM: "bg-[var(--candy-purple)] text-white",
};

export default function ProductCard({ product, index = 0 }) {
  const tilt = index % 2 === 0 ? "rotate(-1deg)" : "rotate(1deg)";
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleAddToCart = async (e) => {
    e.preventDefault(); // Prevent navigation to product detail
    if (!user) {
      toast.error("Please login to add items to your cart.");
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      await addToCart(user.id, product.id);
      toast.success(`${product.name} added to cart!`);
    } catch (err) {
      toast.error(err.message || "Failed to add to cart");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="nb-card group flex flex-col"
      style={{ transform: tilt }}
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`} className="block relative">
        <div className="relative overflow-hidden aspect-[4/5] border-b-[3px] border-black bg-[var(--muted)]">
          <img
            src={resolveImg(product.images?.[0])}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          {product.badge && (
            <span
              className={`absolute top-3 left-3 px-3 py-1 text-xs font-black uppercase tracking-wider rounded-full nb-border ${BADGE_COLORS[product.badge] || "bg-white text-black"}`}
              style={{ boxShadow: "3px 3px 0 0 #0A0A0A" }}
            >
              {product.badge}
            </span>
          )}
          {product.original_price && product.original_price > product.price && (
            <span className="absolute top-3 right-3 bg-black text-white px-3 py-1 text-xs font-black uppercase rounded-full">
              -{Math.round(((product.original_price - product.price) / product.original_price) * 100)}%
            </span>
          )}
        </div>
      </Link>

      <div className="p-4 md:p-5 flex flex-col gap-3 flex-1">
        <Link to={`/product/${product.id}`} className="font-display font-bold text-lg md:text-xl leading-snug hover:underline decoration-[var(--primary)] decoration-[3px] underline-offset-4">
          {product.name}
        </Link>
        <div className="flex items-baseline gap-2">
          <span className="font-display font-black text-2xl">₹{product.price}</span>
          {product.original_price && product.original_price > product.price && (
            <span className="text-sm line-through text-[var(--muted-foreground,#5C5C5C)]">₹{product.original_price}</span>
          )}
        </div>
        
        <div className="mt-auto grid grid-cols-2 gap-2">
          <button
            onClick={handleAddToCart}
            disabled={loading}
            className="bg-black text-white nb-btn !py-2.5 !text-xs md:!text-sm flex items-center justify-center gap-2 hover:bg-[var(--primary)] transition-colors"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <ShoppingCart size={16} />}
            Cart
          </button>
          <a
            href={waProductLink(product)}
            target="_blank"
            rel="noreferrer"
            data-testid={`product-card-cta-${product.id}`}
            className="bg-[var(--whatsapp)] text-white nb-btn !py-2.5 !text-xs md:!text-sm flex items-center justify-center gap-2"
          >
            WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}
